import React, { FC, useEffect, useRef, useState } from 'react'
import { secToHourMinute } from '../../../utils/date'

interface Props {
  list: {
    label: string
    value: number
    color: string
  }[]
  graphTitle: string
  chartSize: number
  displayOuterLabel?: boolean
  hoverName?: string
}

interface GraphList {
  title: string
  value: number
  fill: string
  bg_fill: string
}

const SvgPieChart: FC<Props> = (props) => {
  const {
    list,
    graphTitle,
    chartSize,
    displayOuterLabel,
    hoverName,
  } = props

  const tipRef = useRef<HTMLDivElement>(null)
  const [isDisplayTip, setIsDisplayTip] = useState<boolean>(false)
  const [focusTitle, setFocusTitle] = useState<string>("")

  const brightness = (rgb: string) => (
    (parseInt(rgb.substring(1, 3), 16) * 0.299)
    + (parseInt(rgb.substring(3, 5), 16) * 0.587)
    + (parseInt(rgb.substring(5, 7), 16) * 0.114)
  )

  const displayTip = (data: GraphList, angle: number, sum: number) => {
    if (!tipRef.current) return
    tipRef.current.style.left = `${(chartSize * 0.5) + ((chartSize * 0.7) * Math.cos((sum + (angle / 2) - 90) * (Math.PI / 180)))}px`
    tipRef.current.style.top = `${(chartSize * 0.5) + ((chartSize * 0.7) * Math.sin((sum + (angle / 2) - 90) * (Math.PI / 180)))}px`
    tipRef.current.style.backgroundColor = `${data.fill === "transparent" ? "#C61616" : data.fill}`
    tipRef.current.style.color = brightness(data.fill) >= 140 ? '#000000' : '#FFFFFF'
    tipRef.current.innerHTML = `<div>${data.title} </div><div style="font-size:16px;">${secToHourMinute(data.value || 0)}</div>`
    setIsDisplayTip(true)
  }

  const hiddenTip = () => {
    setIsDisplayTip(false)
  }

  const d = (cx: number, cy: number, r: number, a: number, s: number) => {
    const rad1 = ((s - 90) / 180) * Math.PI
    const rad2 = ((a + s - 90) / 180) * Math.PI
    const params = [
      cx + r * Math.cos(rad1), cy + r * Math.sin(rad1),
      r, r,
      a > 180 ? 1 : 0,
      cx + r * Math.cos(rad2), cy + r * Math.sin(rad2),
      cx, cy,
    ]

    return params.reduce((str, param) => str.replace('%d', String(param)), 'M %d,%d A %d,%d 0 %d,1 %d,%d L %d,%d Z')
  }

  const clickArcArea = (title: string) => {
    if (focusTitle === title) {
      setFocusTitle("")
    } else {
      setFocusTitle(title)
    }
  }

  const arc = (svg: HTMLElement | null, cx: number, cy: number, r: number, graphList: GraphList[], focus?: string) => {
    if (!svg) return
    svg.innerHTML = ""
    let s = 0

    if (!graphList.filter((v) => v.value).length) {
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
      const a = 359.999

      path.setAttribute('d', d(cx, cy, r, a, s))
      path.style.opacity = "1"
      path.setAttribute('fill', "#ECF0F5")
      path.setAttribute('stroke', '#fff')
      svg.appendChild(path)
      return
    }

    const total = graphList.reduce((sum, v) => sum + v.value, 0)
    graphList.filter((v) => v.value).forEach((data: GraphList) => {
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
      const a = graphList.filter((v) => v.value).length === 1 ? 359.999 : (data.value / total) * 360
      const sum = s

      if (data.title === focus) {
        path.setAttribute('d', d(cx, cy, r, a, s))
        path.style.opacity = "1"
      } else {
        path.setAttribute('d', d(cx, cy, r, a, s))
        if (focus) {
          path.style.opacity = "0.2"
        } else {
          path.style.opacity = "1"
        }
      }
      path.setAttribute('fill', data.fill)
      path.setAttribute('stroke', '#fff')
      path.onclick = () => clickArcArea(data.title)
      path.onmouseover = () => displayTip(data, a, sum)
      path.onmouseout = () => hiddenTip()
      path.style.cursor = "pointer"
      path.style.strokeWidth = "0.2px"
      svg.appendChild(path)
      s += a
    })
  }

  const outerLabel = (wrap: HTMLElement | null, dataList: GraphList[], type: string, focus?: string) => {
    if (!wrap) return
    wrap.innerHTML = ""
    let total = 0
    let sum = 0

    dataList.forEach((data: GraphList) => {
      total += data.value
    })
    dataList.forEach((data: GraphList) => {
      if (!data.value) return
      const div = document.createElement('div')
      const span = document.createElement('span')
      const div2 = document.createElement('div')
      const percentage = (data.value / total) * 100
      if (percentage < 3) return
      const angle = percentage * 3.6
      const focusAdd = data.title === focus ? 10 : 0
      const fontSize = data.title === focus ? 24 : 12

      span.textContent = `${Math.round(percentage * 10) / 10}`
      span.style.fontSize = `${fontSize}px`

      div2.textContent = `${data.title}`

      div.style.position = "absolute"
      div.style.fontSize = "10px"
      div.style.color = "#333333"
      div.style.transform = "translateX(-50%) translateY(-50%)"
      div.style.top = `${(chartSize / 2) + ((((chartSize / 2) + 20) + focusAdd) * Math.sin((sum + (angle / 2) - 90) * (Math.PI / 180)))}px`
      div.style.left = `${(chartSize / 2) + ((((chartSize / 2) + 20) + focusAdd) * Math.cos((sum + (angle / 2) - 90) * (Math.PI / 180)))}px`
      div.style.textAlign = "center"
      div.style.whiteSpace = "nowrap"
      div.style.zIndex = "3"
      div.innerHTML = `${div2.outerHTML}${span.outerHTML}%`

      wrap.appendChild(div)
      sum += angle
    })
  }

  useEffect(() => {
    let graphList:GraphList[] = []
    graphList = list.map((v) => (
      {
        title: v.label,
        value: v.value,
        fill: v.color || "#333",
        bg_fill: v.color || "#CCC",
      }
    ))

    arc(document.getElementById(`svg_graph_${graphTitle}`), (chartSize * 0.5), (chartSize * 0.5), (chartSize * 0.5), graphList, hoverName)
    if (displayOuterLabel) {
      outerLabel(document.getElementById(`svg_label_${graphTitle}`), graphList, "current", focusTitle)
    }
  }, [list, hoverName])

  return (
    <div
      style={{
        position: "relative",
      }}
    >
      <div
        style={{
          position: "absolute",
          width: `${chartSize}px`,
          height: `${chartSize}px`,
          borderRadius: "50%",
          backgroundColor: "#ffe8e8",
          backgroundImage: "radial-gradient(#ff4d4d 1%, transparent 21%),radial-gradient(#ff4d4d 1%, transparent 21%)",
          backgroundPosition: "0 0, 2px 2px",
          backgroundSize: "4px 4px",
          zIndex: 1,
        }}
      />
      <svg
        xmlns="http://www.w3.org/2000/svg"
        version="1.1"
        width={chartSize}
        height={chartSize}
        id={`svg_graph_${graphTitle}`}
        style={{
          zIndex: 2,
          position: "relative",
        }}
      />
      <div id={`svg_label_${graphTitle}`} />
      <div
        className="display_tip"
        style={{
          opacity: `${isDisplayTip ? 1 : 0}`,
          position: "absolute",
          transform: "translateX(-50%) translateY(-50%)",
          padding: "4px 8px",
          border: "2px solid white",
          pointerEvents: "none",
          whiteSpace: "nowrap",
          zIndex: 3,
          textAlign: "center",
        }}
        ref={tipRef}
      />
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translateX(-50%) translateY(-50%)",
          zIndex: 2,
          width: `${chartSize * 0.7}px`,
          height: `${chartSize * 0.7}px`,
          backgroundColor: "#FFFFFF",
          borderRadius: "50%",
        }}
      />
      {
        !!graphTitle && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translateX(-50%) translateY(-50%)",
              zIndex: 2,
              whiteSpace: "nowrap",
              backgroundColor: "#FFFFFF",
              border: "solid 1px #666",
              padding: "2px 4px",
              fontSize: "10px",
            }}
          >
            {graphTitle}
          </div>
        )
      }
    </div>
  )
}

SvgPieChart.defaultProps = {
  displayOuterLabel: false,
  hoverName: "",
}

export default SvgPieChart
